import React from 'react';
import {
  LoadingIndicator,
  IconPlay,
  IconArrowRight,
} from '@spotify-internal/encore-web';
import { useRouter } from 'next/router';
import { useTracking } from '../Tracking';
import { ContentfulImage } from '../Contentful/ContentfulImage';
import type { SearchHit } from '../Search/types';
import { itemToSearchHit } from '../Search/content';
import { formatText, fixHref } from '../utils';
import { DEFAULT_SOCIAL_CARD } from '../Layout/PageHead';
import {
  StyledLink,
  Headline,
  Subtext,
  CTALink,
  Item,
  ItemList,
  ImageWrapper,
  ItemLabel,
  ItemTitle,
  ItemText,
  LoadMoreButton,
  LoadMoreButtonWrapper,
} from './index.styles';
import { Box, Supertext } from '../ui';
import { useLocale, useT } from '../../../features/src/i18n';
import styled from 'styled-components';
import { ModuleType } from '../Module/types';

type ContentGridType = {
  trackCategory: string;
  supertext?: string;
  headline?: string;
  subtext?: string;
  ctaLink?: string;
  ctaText?: string;
  items?: any[];
  featuredItems?: number;
  getPageItems?: (page: number) => Promise<any[]>;
  hasNextPage?: (curPage: number) => boolean;
  placeholders?: boolean;
};

// Hide entire module when box will be empty
export const StyledBox = styled(Box)<{ displayModule: boolean }>`
  height: ${props => (props.displayModule ? '' : '0px')};
  padding: ${props => (props.displayModule ? '' : '0px')};
  opacity: ${props => (props.displayModule ? '' : '0%')};
`;

export const ContentGrid = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  ContentGridType
>(
  (
    {
      supertext,
      headline,
      subtext,
      ctaText,
      ctaLink,
      trackCategory,
      items: initialItems = [],
      featuredItems = 0,
      getPageItems = () => Promise.resolve([]),
      hasNextPage = () => false,
      placeholders = true,
    },
    ref,
  ) => {
    const t = useT();
    const locale = useLocale();
    const router = useRouter();
    const { sendEvent } = useTracking();
    const [items, setItems] = React.useState<SearchHit[]>([]);
    const page = React.useRef(1);
    const [pageLoading, setPageLoading] = React.useState(false);

    React.useEffect(() => {
      const newItems = initialItems
        .map(item => itemToSearchHit(item, locale))
        .filter(Boolean) as NonNullable<SearchHit[]>;
      setItems(newItems);
    }, [initialItems]);

    const loadNextPage = async (e: React.MouseEvent) => {
      e.preventDefault();
      setPageLoading(true);
      sendEvent({
        eventCategory: `Frodor - ${trackCategory}`,
        eventAction: 'click',
        eventLabel: `Load More - Page ${page.current + 1}`,
      });
      const pageItems = await getPageItems(++page.current);
      const newItems = pageItems
        .map(item => itemToSearchHit(item, locale))
        .filter(Boolean) as NonNullable<SearchHit[]>;
      setItems([...items, ...newItems]);
      setPageLoading(false);
    };

    return (
      <StyledBox
        ref={ref as React.RefObject<HTMLDivElement>}
        displayModule={items?.length !== 0}
      >
        {supertext && <Supertext>{supertext}</Supertext>}
        {headline && <Headline>{headline}</Headline>}
        {subtext && (
          <Subtext dangerouslySetInnerHTML={{ __html: formatText(subtext) }} />
        )}
        {ctaText && ctaLink && (
          <CTALink
            href={fixHref(ctaLink)}
            track={[trackCategory, `CTA Link - ${ctaText}`]}
          >
            {ctaText}
            <IconArrowRight iconSize={16} />
          </CTALink>
        )}

        <ItemList>
          {(!items && <LoadingIndicator />) ||
            items.map((item, index) => (
              <Item key={item.id} featured={index < featuredItems}>
                <StyledLink
                  href={item.url}
                  track={[
                    trackCategory,
                    `Content Link - [${item.contentType}]: ${item.title}`,
                  ]}
                  aria-label={`${t(
                    'FRODOR_a55c16',
                    'Go to {title}',
                    'Go to means to click a link to follow it',
                    {
                      title: item.title,
                    },
                  )}`}
                >
                  <ImageWrapper
                    video={
                      item.contentType === 'video' ||
                      item.contentType === 'videoPage'
                    }
                    featured={index < featuredItems}
                  >
                    {(item.image && (
                      <ContentfulImage
                        asset={item.image}
                        placeholder={placeholders}
                      >
                        <IconPlay />
                      </ContentfulImage>
                    )) || (
                      // placeholder if no image, for now
                      <img
                        data-src={DEFAULT_SOCIAL_CARD}
                        alt={`${t(
                          'FRODOR_bfbc75',
                          'Spotify for Artists',
                          '',
                        )} - ${item.contentType}`}
                        className="lazyload"
                      />
                    )}
                  </ImageWrapper>

                  {item.label && <ItemLabel>{item.label}</ItemLabel>}

                  {item.title && <ItemTitle>{item.title}</ItemTitle>}
                </StyledLink>

                {item.description && <ItemText>{item.description}</ItemText>}
              </Item>
            ))}
        </ItemList>
        {(hasNextPage(page.current) && Boolean(items?.length)) ||
          (router.query && router.query._lqa === 'loadMore' && (
            <LoadMoreButtonWrapper>
              {pageLoading ? (
                <LoadingIndicator />
              ) : (
                <LoadMoreButton onClick={loadNextPage}>
                  {t(
                    'FRODOR_CONTENT_GRID_LOAD_MORE',
                    'Load more',
                    'If clicked, this button will show the next page of content',
                  )}
                </LoadMoreButton>
              )}
            </LoadMoreButtonWrapper>
          ))}
      </StyledBox>
    );
  },
);
